/**
 * Cypress / debug: skip Yup when the page URL carries `novalidate`.
 * Zero imports so webpack cannot serve a stale copy via trialFormUtils.
 * Reads href + search + document.URL — not React state, not `isBrowser`.
 */

export const searchHasNovalidate = (value = '') => {
  if (value == null || value === '') {
    return false;
  }
  const str = String(value);
  if (str.indexOf('novalidate=') !== -1) {
    return true;
  }
  return /(?:^|[?&#])novalidate(?:=|&|$|#)/.test(str);
};

const liveUrlHasNovalidate = (extraSearch = '') => {
  const parts = [extraSearch];
  if (typeof window !== 'undefined') {
    parts.push(
      window.location.href,
      window.location.search,
      window.location.hash
    );
  }
  if (typeof document !== 'undefined' && document.URL) {
    parts.push(document.URL);
  }
  return parts.some(searchHasNovalidate);
};

/**
 * Snapshot `novalidate` from the real window URL. Call from `onClientEntry`
 * (before React) so a later replace that drops unknown params cannot hide it.
 */
export const latchTrialNovalidateFromWindow = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  const found = liveUrlHasNovalidate();
  window.__trialNovalidate = found;
  if (found && typeof document !== 'undefined') {
    document.documentElement.dataset.novalidate = '1';
  }
  return found;
};

/** Client-side navigation: promote to true if the new location has the param. Never clears. */
export const promoteTrialNovalidateFromSearch = (search = '') => {
  if (typeof window === 'undefined') {
    return;
  }
  if (liveUrlHasNovalidate(search)) {
    window.__trialNovalidate = true;
    if (typeof document !== 'undefined') {
      document.documentElement.dataset.novalidate = '1';
    }
  }
};

/**
 * Stamp `novalidate` onto `<html data-novalidate="1">` as soon as we see it.
 * Enterprise takes longer to fill than /choose; QueryParamProvider can drop
 * unknown params before submit. The dataset survives that replace.
 */
export const persistNovalidateOnDocument = (extraSearch = '') => {
  if (typeof document === 'undefined') {
    return false;
  }
  const found = document.documentElement.dataset.novalidate === '1'
    || (typeof window !== 'undefined' && window.__trialNovalidate === true)
    || liveUrlHasNovalidate(extraSearch);
  if (found) {
    document.documentElement.dataset.novalidate = '1';
    if (typeof window !== 'undefined') {
      window.__trialNovalidate = true;
    }
  }
  return found;
};

export const pageUrlHasNovalidate = (extraSearch = '') => {
  if (typeof document !== 'undefined' && document.documentElement.dataset.novalidate === '1') {
    return true;
  }
  if (typeof window !== 'undefined' && window.__trialNovalidate === true) {
    return true;
  }
  return liveUrlHasNovalidate(extraSearch);
};

/** Submit-time skip: dataset latch, window latch, then live href/search. */
export const shouldSkipTrialYup = (extraSearch = '') => pageUrlHasNovalidate(extraSearch);
